import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Elabels, mapCommentsToDataSource } from '../../../helpers';
import { useCase } from '../../../hooks';
import { FormModal } from 'components/molecules/Modal';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { IEditTaskFormValue } from 'models/cases';
import { Loader } from 'components/atoms/Loader';
import { useDictionary } from 'models/dictionaryContext';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { CustomizedAccordions } from 'components/molecules/CustomizedAccordions';
import { ETaskStatus } from 'constants/Case';
import { IDictionary } from 'models/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';

const initialFormValues = {
  comment: '',
  statusUniqueId: '',
};

interface ITask {
  taskId: string;
  canEdit: boolean;
  onClose: () => void;
  updateTasksData: () => void;
}

const TaskEditForm = ({ taskId, canEdit, onClose, updateTasksData }: ITask) => {
  const { t } = useTranslations();
  const { getTaskData, task, addTaskComment } = useCase();
  const [isLoading, setIsLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const { selectedLanguage } = useLang();
  const { getTaskStatus, taskStatus } = useDictionary();
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      if (!taskStatus) {
        await getTaskStatus();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const sortTaskStatus = (tasks: IDictionary[] | null): IDictionary[] => {
    if (!tasks) return [];
    return [...tasks].sort((a, _) => (a.uniqueId === ETaskStatus.WAITING ? -1 : 1));
  };

  useEffect(() => {
    (async () => {
      if (taskId) {
        await getTaskData(taskId);
        setIsLoading(false);
      }
    })();
  }, [taskId]);

  const schema = yup.object({
    comment: yup.string().required(t('general.field.validation.required')),
    statusUniqueId: yup.string().required(t('general.field.validation.required')),
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    getValues,
  } = useForm<IEditTaskFormValue>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IEditTaskFormValue>,
  });

  useEffect(() => {
    if (task?.statusUniqueId) {
      setValue('statusUniqueId', task.statusUniqueId.toString());
    } else if (!task && taskStatus?.length) {
      setValue('statusUniqueId', taskStatus[1].uniqueId.toString());
    }
  }, [task, taskStatus, setValue]);

  const onSubmit = async (data: IEditTaskFormValue) => {
    const res = await addTaskComment(taskId, data);
    if (res) {
      onClose();
      reset(initialFormValues);
      updateTasksData();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormModal
          open={!!taskId}
          disableBackdropClick={true}
          onClickClose={() => {
            onClose();
            reset(initialFormValues);
          }}
          minWidth="80%"
          title={t(Elabels.viewTask)}
          titleLeft
          onClickApprove={
            isEdit ? handleSubmit(onSubmit) : canEdit ? () => setIsEdit(true) : undefined
          }
          approveButtonLabel={!isEdit && canEdit ? t(Elabels.submitUpdate) : undefined}
          cancelButtonLabel={isEdit ? t('general.cancel.button') : t('general.close.label')}
        >
          {isLoading && <Loader isLoading />}
          <Grid container sx={{ gap: 3 }}>
            {!isLoading && (
              <>
                <Grid container sx={{ gap: 2.5 }}>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.taskName)}
                      </Typography>
                      <Typography variant="body2">{task?.name}</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.taskDescription)}
                      </Typography>
                      <Typography variant="body2">{task?.description}</Typography>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }} container sx={{ rowGap: 1 }}>
                  <Grid
                    size={{ xs: 12 }}
                    sx={{ justifyContent: !isEdit && canEdit ? 'end' : 'start' }}
                  >
                    {isEdit && (
                      <Typography variant="subtitle1"> {t(Elabels.results)}</Typography>
                    )}
                  </Grid>
                  {!!task?.comments?.length && (
                    <Grid size={{ xs: 12 }}>
                      <CustomizedAccordions
                        items={mapCommentsToDataSource(task?.comments, longDateFormat, t)}
                        maxVisibleItems={6}
                        defaultExpandedIndex={task?.comments?.length - 1}
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}
            {isEdit && (
              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }}>
                  <FormInput
                    name="comment"
                    type={EFormInputTypes.textarea}
                    minRows={5}
                    multiline
                    label={t(Elabels.comment)}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    charsLimit={1000}
                    showCounter
                    required
                    id="task_edit_form_comment-input"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormRadioGroup
                    name="statusUniqueId"
                    label={t(Elabels.taskStatus)}
                    options={mapDictionaryToOptionProp(sortTaskStatus(taskStatus))}
                    control={control}
                    errors={errors}
                    required
                    selectedByDefault={ETaskStatus.WAITING}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </FormModal>
      </form>
    </>
  );
};

export default TaskEditForm;
